import React, { useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import Icon from './AppIcon';

/**
 * Reusable uploaded image thumbnail with:
 * - Orange glassmorphic delete button (top-right, visible on hover)
 * - Hover-to-enlarge preview rendered via React Portal into document.body,
 *   escaping all stacking contexts (backdrop-filter, transform, overflow, etc.)
 *
 * Props:
 *   src        – image URL (required)
 *   alt        – alt text (optional, defaults to "Uploaded image")
 *   onRemove   – callback when delete is clicked (optional; hides button if omitted)
 *   className  – extra classes on the outer wrapper (optional)
 *   imgClass   – classes on the <img> element (optional, defaults to sensible value)
 *   height     – tailwind height class for the thumbnail, e.g. "h-20" (default "h-20")
 *   previewWidth – tailwind width class for the hover preview image (default "w-[400px]")
 */
const UploadedImageThumb = ({
  src,
  alt = 'Uploaded image',
  onRemove,
  className = '',
  imgClass,
  height = 'h-20',
  previewWidth = 'w-[400px]',
}) => {
  const [broken, setBroken] = useState(false);
  const [previewPos, setPreviewPos] = useState(null);
  const thumbRef = useRef(null);
  const defaultImgClass = `w-full ${height} object-cover rounded-lg border border-white/10`;

  if (broken) return null;

  const handleMouseEnter = () => {
    if (thumbRef.current) {
      const rect = thumbRef.current.getBoundingClientRect();
      setPreviewPos({ x: rect.left, y: rect.top });
    }
  };

  const handleMouseLeave = () => {
    setPreviewPos(null);
  };

  return (
    <div
      ref={thumbRef}
      className={`relative group inline-block w-fit ${className}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <img
        src={src}
        alt={alt}
        className={imgClass || defaultImgClass}
        onError={() => setBroken(true)}
      />

      {/* Delete button */}
      {onRemove && (
        <button
          onClick={onRemove}
          className="absolute -top-1.5 -right-1.5 w-6 h-6 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity backdrop-blur-md bg-orange-500/30 border border-orange-500/50 hover:bg-orange-500/50 z-10"
        >
          <Icon name="X" size={10} className="text-white" />
        </button>
      )}

      {/* Hover enlarge preview — rendered via portal into document.body to escape
          all stacking contexts created by backdrop-filter / transform / overflow */}
      {previewPos && ReactDOM.createPortal(
        <div
          className="fixed z-[9999] pointer-events-none"
          style={{
            left: previewPos.x,
            top: previewPos.y,
            transform: 'translateY(-100%) translateY(-8px)',
          }}
        >
          <div className="rounded-xl overflow-hidden border border-white/20 shadow-[0_8px_32px_rgba(0,0,0,0.6)] bg-black">
            <img
              src={src}
              alt={alt}
              className={`${previewWidth} max-w-[50vw] h-auto object-contain`}
            />
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default UploadedImageThumb;
